import _ from "lodash"

const METADATA_KEYS = new Set(["date_created", "date_changed"])

// Checks if a string represents an empty serialized structure (like "[]", "{}", or "null").
const isSerializedEmptyStructure = (value) => {
  if (typeof value !== "string") return false

  const trimmed = value.trim()
  if (trimmed.length === 0) return false

  // Treat stringified empty payloads as empty so placeholder rows are removed.
  if (!["[]", "{}", "null"].includes(trimmed)) return false

  try {
    const parsed = JSON.parse(trimmed)
    return isValueEmpty(parsed)
  } catch (error) {
    return false
  }
}

// Recursively checks if a value is empty, ignoring metadata fields.
const isValueEmpty = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === "string") {
    if (value.trim() === "") return true
    if (isSerializedEmptyStructure(value)) return true
    return false
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return true
    return value.every((item) => isValueEmpty(item))
  }
  if (typeof value === "object" && value !== null) {
    return Object.keys(value).every((key) => {
      if (METADATA_KEYS.has(key)) return true
      return isValueEmpty(value[key])
    })
  }
  return false
}

/**
 * Removes empty blocks from multi-field arrays within a form's values.
 * A block is considered empty if all its fields (ignoring metadata like `date_created`)
 * are null, undefined, or an empty string. This function mutates the `values` object directly.
 * @param {object} values The form values object.
 * @param {Array} fieldDef The field definitions for the form block.
 * @returns {object} The mutated `values` object.
 */
export const cleanMultiFieldValues = (values, fieldDef) => {
  const multiFields = _.filter(fieldDef, { field_type: "multi_field" })
  multiFields.forEach((mf) => {
    const fieldName = mf.field_name
    const fieldValue = values[fieldName]

    // Self-heal legacy malformed records where multi_field was saved as an object.
    if (_.isNil(fieldValue)) {
      // If the multi-field value is missing, initialize it as an empty array.
      values[fieldName] = []
    } else if (_.isPlainObject(fieldValue)) {
      // If the multi-field value is a plain object, wrap it in an array to normalize it.
      values[fieldName] = [fieldValue]
    } else if (!Array.isArray(fieldValue)) {
      // If the multi-field value is not an array, initialize it as an empty array.
      // This handles any unexpected types by resetting the multi-field to an empty array.
      // This is the safest way to ensure the multi-field value is always an array.
      values[fieldName] = []
    }

    // Remove entirely empty blocks from the multi-field array.
    values[fieldName] = values[fieldName].filter((block) => {
      if (!block) return false
      const relevantKeys = Object.keys(block).filter(
        (key) => !METADATA_KEYS.has(key),
      )
      return !relevantKeys.every((key) => isValueEmpty(block[key]))
    })
  })

  return values
}
